import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ChangeResourceUserModificationType } from '~/types';
import {
  dateToMidnightUTCObject,
  getLuxonJsDateFormatFromMe,
  mapRepliconDateToUtcObject
} from '~/modules/common/dates/convert';
import { delay } from '~/modules/common/util';
import { useSnackbar } from '~/modules/common/snackbar';
import { useMeContext } from '~/modules/me/useMeContext';

export const CHANGE_RESOURCE_USER = gql`
  mutation useChangeResourceUser($input: ChangeResourceUserInput!) {
    changeResourceUser(input: $input) {
      newResourceAllocationId
    }
  }
`;

export const useOnSubmit = ({
  existingResourceUserId,
  onChangeResourceSuccess,
  onClose,
  projectId
}) => {
  const [changeResourceUser] = useMutation(CHANGE_RESOURCE_USER);
  const me = useMeContext();
  const {
    featureFlags: { isPsaRmp2025Q4WcagEnabled }
  } = me;
  const { showSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const dateFormat = getLuxonJsDateFormatFromMe(me);

  return {
    onChangeResourceUserSubmit: useCallback(
      async (values, { setSubmitting }) => {
        const { asOfDate, modificationType, newResourceUser } = values;

        const normalizedAsOfDate =
          typeof asOfDate === 'string'
            ? dateToMidnightUTCObject(asOfDate)
            : mapRepliconDateToUtcObject(asOfDate);

        setSubmitting(true);
        await changeResourceUser({
          variables: {
            input: {
              asOfDate:
                modificationType === ChangeResourceUserModificationType.Asof
                  ? normalizedAsOfDate
                  : null,
              existingResourceUserId,
              modificationType,
              newResourceUserId: newResourceUser?.id,
              projectId
            }
          }
        });
        await delay(2000);
        setSubmitting(false);

        onChangeResourceSuccess();

        if (isPsaRmp2025Q4WcagEnabled) {
          const isAsOfModification =
            modificationType === ChangeResourceUserModificationType.Asof;
          const messageId = isAsOfModification
            ? 'resourceAllocationChangeSnackBar.resourceChangedAsOf'
            : 'resourceAllocationChangeSnackBar.resourceChanged';

          const messageValues = { username: newResourceUser?.displayText };

          if (isAsOfModification) {
            messageValues.date = normalizedAsOfDate.toFormat(dateFormat) || '';
          }

          showSnackbar(formatMessage({ id: messageId }, messageValues));
        }

        onClose();
      },
      [
        changeResourceUser,
        existingResourceUserId,
        onChangeResourceSuccess,
        onClose,
        projectId,
        showSnackbar,
        formatMessage,
        isPsaRmp2025Q4WcagEnabled,
        dateFormat
      ]
    )
  };
};

export default useOnSubmit;
